import { Command } from 'commander';
import Table from 'cli-table3';
import chalk from 'chalk';
import inquirer from 'inquirer';
import { WorkflowEngine } from '../core/workflow/workflow-engine';
import { WorkflowTriggerManager, TriggerType } from '../core/workflow/workflow-trigger-manager';
import { getBuiltinTriggers } from '../core/workflow/builtin-triggers';
import { HookManager } from '../core/hooks/hook-manager';
import { TaskStore } from '../core/task-store';
import { ConfigManager } from '../core/config-manager';
import { logger } from '../utils/logger';

export function createTriggersCommand(): Command {
  const command = new Command('triggers');
  
  command
    .description('管理智能工作流触发器')
    .addCommand(createListCommand())
    .addCommand(createEnableCommand())
    .addCommand(createDisableCommand())
    .addCommand(createStatusCommand())
    .addCommand(createTestCommand());
  
  return command;
}

function createListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('列出所有可用的工作流触发器')
    .option('-t, --type <type>', '按类型过滤')
    .option('-e, --enabled', '只显示启用的触发器')
    .action(async (options) => {
      try {
        const triggers = getBuiltinTriggers();
        let filtered = triggers;
        
        // 过滤
        if (options.type) {
          filtered = filtered.filter(t => t.type === options.type);
        }
        
        if (options.enabled) {
          filtered = filtered.filter(t => t.enabled);
        }
        
        // 显示表格
        const table = new Table({
          head: ['ID', '名称', '类型', '状态', '优先级', '工作流'],
          style: { head: ['cyan'] }
        });
        
        for (const trigger of filtered) {
          table.push([
            trigger.id,
            trigger.name,
            chalk.blue(trigger.type),
            trigger.enabled ? chalk.green('启用') : chalk.gray('禁用'),
            trigger.priority?.toString() || '-',
            chalk.yellow(trigger.workflowId)
          ]);
        }
        
        console.log(table.toString());
        
        // 显示统计
        console.log(`\n总计: ${filtered.length} 个触发器`);
        
        // 显示类型说明
        console.log('\n' + chalk.bold('触发器类型:'));
        console.log('- file_change: 文件变更触发');
        console.log('- task_status_change: 任务状态变化触发');
        console.log('- document_approval: 文档审批触发');
        console.log('- ears_requirement_added: EARS 需求添加触发');
        console.log('- schedule: 定时触发');
        console.log('- webhook: Webhook 触发');
        
      } catch (error: any) {
        logger.error('列出触发器失败:', error.message);
      }
    });
  
  return command;
}

function createEnableCommand(): Command {
  const command = new Command('enable');
  
  command
    .description('启用工作流触发器')
    .argument('[triggerId]', '触发器 ID')
    .action(async (triggerId) => {
      try {
        // 初始化管理器
        const configManager = new ConfigManager();
        const workflowEngine = new WorkflowEngine(configManager);
        const hookManager = new HookManager();
        const taskStore = new TaskStore();
        const triggerManager = new WorkflowTriggerManager(workflowEngine, hookManager, taskStore);
        
        // 注册内置触发器
        const triggers = getBuiltinTriggers();
        for (const trigger of triggers) {
          await triggerManager.registerTrigger(trigger);
        }
        
        // 如果没有指定 ID，让用户选择
        if (!triggerId) {
          const disabledTriggers = triggers.filter(t => !t.enabled);
          
          if (disabledTriggers.length === 0) {
            console.log(chalk.yellow('所有触发器都已启用'));
            return;
          }
          
          const { selected } = await inquirer.prompt({
            type: 'list',
            name: 'selected',
            message: '选择要启用的触发器:',
            choices: disabledTriggers.map(t => ({
              name: `${t.name} (${t.type})`,
              value: t.id
            }))
          });
          
          triggerId = selected;
        }
        
        // 启用触发器
        await triggerManager.enableTrigger(triggerId);
        console.log(chalk.green(`✅ 已启用触发器: ${triggerId}`));
        
        // 显示配置提示
        const trigger = triggers.find(t => t.id === triggerId);
        if (trigger) {
          console.log('\n' + chalk.bold('触发器配置:'));
          console.log(`类型: ${trigger.type}`);
          console.log(`工作流: ${trigger.workflowId}`);
          
          if (trigger.debounce) {
            console.log(`防抖延迟: ${trigger.debounce}ms`);
          }
          
          if (trigger.cooldown) {
            console.log(`冷却时间: ${trigger.cooldown}ms`);
          }
        }
        
      } catch (error: any) {
        logger.error('启用触发器失败:', error.message);
      }
    });
  
  return command;
}

function createDisableCommand(): Command {
  const command = new Command('disable');
  
  command
    .description('禁用工作流触发器')
    .argument('[triggerId]', '触发器 ID')
    .action(async (triggerId) => {
      try {
        // 初始化管理器
        const configManager = new ConfigManager();
        const workflowEngine = new WorkflowEngine(configManager);
        const hookManager = new HookManager();
        const taskStore = new TaskStore();
        const triggerManager = new WorkflowTriggerManager(workflowEngine, hookManager, taskStore);
        
        // 注册内置触发器
        const triggers = getBuiltinTriggers();
        for (const trigger of triggers) {
          await triggerManager.registerTrigger(trigger);
        }
        
        // 如果没有指定 ID，让用户选择
        if (!triggerId) {
          const enabledTriggers = triggers.filter(t => t.enabled);
          
          if (enabledTriggers.length === 0) {
            console.log(chalk.yellow('没有启用的触发器'));
            return;
          }
          
          const { selected } = await inquirer.prompt({
            type: 'list',
            name: 'selected',
            message: '选择要禁用的触发器:',
            choices: enabledTriggers.map(t => ({
              name: `${t.name} (${t.type})`,
              value: t.id
            }))
          });
          
          triggerId = selected;
        }
        
        // 禁用触发器
        await triggerManager.disableTrigger(triggerId);
        console.log(chalk.green(`✅ 已禁用触发器: ${triggerId}`));
        
      } catch (error: any) {
        logger.error('禁用触发器失败:', error.message);
      }
    });
  
  return command;
}

function createStatusCommand(): Command {
  const command = new Command('status');
  
  command
    .description('查看触发器状态')
    .argument('[triggerId]', '触发器 ID')
    .action(async (triggerId) => {
      try {
        // 初始化管理器
        const configManager = new ConfigManager();
        const workflowEngine = new WorkflowEngine(configManager);
        const hookManager = new HookManager();
        const taskStore = new TaskStore();
        const triggerManager = new WorkflowTriggerManager(workflowEngine, hookManager, taskStore);
        
        // 注册内置触发器
        const triggers = getBuiltinTriggers();
        for (const trigger of triggers) {
          await triggerManager.registerTrigger(trigger);
        }
        
        if (triggerId) {
          // 显示特定触发器状态
          const status = triggerManager.getTriggerStatus(triggerId);
          
          if (!status) {
            console.log(chalk.red(`触发器不存在: ${triggerId}`));
            return;
          }
          
          console.log(chalk.bold(`\n触发器状态: ${status.name}`));
          console.log(`ID: ${status.id}`);
          console.log(`类型: ${status.type}`);
          console.log(`状态: ${status.enabled ? chalk.green('启用') : chalk.gray('禁用')}`);
          console.log(`工作流: ${status.workflowId}`);
          console.log(`优先级: ${status.priority || '-'}`);
          
          if (status.lastTriggered) {
            console.log(`最后触发: ${status.lastTriggered}`);
          }
          
          if (status.isDebouncing) {
            console.log(chalk.yellow('状态: 防抖中...'));
          }
          
          if (status.isWatching) {
            console.log(chalk.green('状态: 监听中'));
          }
          
          // 显示条件
          console.log('\n' + chalk.bold('触发条件:'));
          for (const condition of status.conditions) {
            if (condition.pattern) {
              console.log(`- 文件模式: ${condition.pattern}`);
            } else if (condition.expression) {
              console.log(`- 表达式: ${condition.expression}`);
            } else {
              console.log(`- 自定义函数`);
            }
          }
          
        } else {
          // 显示所有触发器的简要状态
          const table = new Table({
            head: ['ID', '名称', '状态', '最后触发'],
            style: { head: ['cyan'] }
          });
          
          for (const trigger of triggers) {
            const status = triggerManager.getTriggerStatus(trigger.id);
            if (status) {
              table.push([
                status.id,
                status.name,
                status.enabled ? chalk.green('启用') : chalk.gray('禁用'),
                status.lastTriggered || '-'
              ]);
            }
          }
          
          console.log(table.toString());
        }
        
      } catch (error: any) {
        logger.error('获取触发器状态失败:', error.message);
      }
    });
  
  return command;
}

function createTestCommand(): Command {
  const command = new Command('test');
  
  command
    .description('测试触发器（模拟触发）')
    .argument('<triggerId>', '触发器 ID')
    .option('-c, --context <json>', '触发上下文 (JSON)')
    .action(async (triggerId, options) => {
      try {
        // 初始化管理器
        const configManager = new ConfigManager();
        const workflowEngine = new WorkflowEngine(configManager);
        const hookManager = new HookManager();
        const taskStore = new TaskStore();
        const triggerManager = new WorkflowTriggerManager(workflowEngine, hookManager, taskStore);
        
        // 查找触发器
        const triggers = getBuiltinTriggers();
        const trigger = triggers.find(t => t.id === triggerId);
        
        if (!trigger) {
          console.log(chalk.red(`触发器不存在: ${triggerId}`));
          return;
        }
        
        console.log(chalk.bold(`测试触发器: ${trigger.name}`));
        
        // 构造测试上下文
        let context: any = {
          type: trigger.type,
          metadata: {
            test: true
          }
        };
        
        if (options.context) {
          try {
            const customContext = JSON.parse(options.context);
            context = { ...context, ...customContext };
          } catch {
            console.log(chalk.red('无效的 JSON 上下文'));
            return;
          }
        } else {
          // 根据类型提供默认测试数据
          switch (trigger.type) {
            case TriggerType.FILE_CHANGE:
              context.filePath = '.workflow/test.md';
              break;
            case TriggerType.TASK_STATUS_CHANGE:
              context.taskId = 'test-task-001';
              context.metadata.oldStatus = 'in_progress';
              context.metadata.newStatus = 'done';
              break;
            case TriggerType.DOCUMENT_APPROVAL:
              context.documentId = 'test-doc-001';
              context.metadata.documentType = 'prd';
              context.metadata.approved = true;
              break;
            case TriggerType.EARS_REQUIREMENT_ADDED:
              context.metadata.requirement = {
                id: 'REQ-TEST-001',
                type: 'ubiquitous',
                priority: 'must',
                shall: 'test requirement'
              };
              break;
          }
        }
        
        console.log('\n' + chalk.bold('测试上下文:'));
        console.log(JSON.stringify(context, null, 2));
        
        // 确认测试
        const { confirm } = await inquirer.prompt({
          type: 'confirm',
          name: 'confirm',
          message: '确定要触发此工作流吗？',
          default: false
        });
        
        if (!confirm) {
          console.log(chalk.yellow('已取消'));
          return;
        }
        
        // 注册并触发
        await triggerManager.registerTrigger(trigger);
        
        console.log(chalk.yellow('\n模拟触发中...'));
        
        // 直接调用私有方法进行测试（实际使用中应该暴露测试接口）
        // 这里简化处理，只显示信息
        console.log(chalk.green(`\n✅ 测试完成！`));
        console.log(`触发器 ${triggerId} 将会启动工作流: ${trigger.workflowId}`);
        
        if (trigger.variables) {
          console.log('\n' + chalk.bold('工作流变量:'));
          console.log(JSON.stringify(trigger.variables, null, 2));
        }
        
      } catch (error: any) {
        logger.error('测试触发器失败:', error.message);
      }
    });
  
  return command;
}